import type { UiMessageType } from '~/shared/constants/uiMessage'
import type { UiMessage } from './index.h'

import { createEvent, createStore, sample } from 'effector'
import { requestsPolicyNotification } from '~/features/policy-message'
import { UI_MESSAGE_TYPE } from '~/shared/constants/uiMessage'
import { footerModel } from '~/shared/ui/components/Footer'
import { getPriority, NO_MESSAGE } from './index.h'

/** Event to add or update a message in the message queue */
export const setNewMessage = createEvent<{
  key: string
  message: UiMessage
  type: UiMessageType
}>()

/** Internal event to update both messageToShow and messages stores */
const updates = createEvent<{
  message: UiMessage
  messages: Record<string, UiMessage>
}>()

/** Store of the currently displayed message based on priority */
export const $messageToShow = createStore<UiMessage>(NO_MESSAGE).on(
  updates,
  (_, { message }) => message
)

/** Store of all active messages indexed by key */
export const $messages = createStore<Record<string, UiMessage>>({}).on(
  updates,
  (_, { messages }) => messages
)

/**
 * Message processing sample to show one exact message or nothing at all.
 */
sample({
  clock: setNewMessage,
  source: $messages,
  fn: (messages, { key, message, type }) => {
    // Set the message type
    message.type = type
    // Store the updated message with all others
    const updatedMessages = {
      ...messages,
      [key]: message,
    }

    let messageToShow: UiMessage = NO_MESSAGE
    let highestPriorityFound = -1

    // Find the highest priority message to display
    for (const [_, msg] of Object.entries(updatedMessages)) {
      // Skip messages that shouldn't be shown
      if (!msg.show) continue

      const priority = getPriority(msg.type)

      // If this message has higher priority, use it
      if (priority > highestPriorityFound) {
        highestPriorityFound = priority
        messageToShow = msg
      }
    }

    return { message: messageToShow, messages: updatedMessages }
  },
  target: updates,
})

// Messages could be added from models or from ui using hooks
sample({
  clock: requestsPolicyNotification.$message,
  fn: (message) => ({ key: 'policy', message, type: UI_MESSAGE_TYPE.WARNING }),
  target: setNewMessage,
})

/**
 * Footer height adjustment when message is displayed
 */

export const setMessageHeight = createEvent<number | null>()

/** Adjust footer height to accommodate message component */
footerModel.$adjustHeight.on(setMessageHeight, (_, value) => value)
